// WebSocketManager.js
export default class WebSocketManager {
  constructor(url, options = {}) {
    this.url = url;
    this.options = options;
    this.socket = null;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = options.maxReconnectAttempts || 10;
    this.reconnectTimeoutId = null;
    this.listeners = {
      message: [],
      open: [],
      close: [],
      error: [],
      reconnecting: [],
      statusChange: []
    };
    
    this._status = 'disconnected';
    this.intentionallyClosed = false;

    // Heartbeat properties
    this.heartbeatInterval = null;
    this.heartbeatTimeout = null;
    this.heartbeatIntervalTime = 30000; // 30 seconds between pings
    this.heartbeatTimeoutTime = 10000;  // 10 seconds to wait for pong
  }
  
  get status() {
    return this._status;
  }
  
  set status(newStatus) {
    if (this._status !== newStatus) {
      this._status = newStatus;
      this.notifyListeners('statusChange', newStatus);
    }
  }
  
  connect() {
    if (this.reconnectTimeoutId) {
      clearTimeout(this.reconnectTimeoutId);
      this.reconnectTimeoutId = null;
    }

    this.intentionallyClosed = false;
    this.cleanup();
    this.status = 'connecting';
    
    try {
      console.log(`Connecting to WebSocket at ${this.url}`);
      this.socket = new WebSocket(this.url);
      this.socket.binaryType = 'arraybuffer';

      this.socket.onopen = (event) => {
        console.log('WebSocket connection opened');
        this.reconnectAttempts = 0;
        this.status = 'connected';
        this.notifyListeners('open', event);
        this.startHeartbeat();
      };
      
      this.socket.onmessage = (event) => {
        if (typeof event.data === 'string') {
          try {
            const data = JSON.parse(event.data);
            if (data.type === 'pong') {
              if (this.heartbeatTimeout) {
                clearTimeout(this.heartbeatTimeout);
                this.heartbeatTimeout = null;
              }
              return;
            }
          } catch (e) {
            // Non-JSON text; pass through.
          }
        }
        this.notifyListeners('message', event);
      };
      
      this.socket.onerror = (event) => {
        console.error('WebSocket error:', event);
        this.notifyListeners('error', event);
      };
      
      this.socket.onclose = (event) => {
        console.log('WebSocket closed:', event.code, event.reason);
        this.notifyListeners('close', event);
        this.stopHeartbeat();

        // Keep retrying unless the caller explicitly hung up via disconnect().
        // The previous `status === 'connected'` guard meant a failed reconnect
        // (status is 'reconnecting' at that point) would give up immediately,
        // so a backend restart never recovered.
        if (!this.intentionallyClosed && this.reconnectAttempts < this.maxReconnectAttempts) {
          this.attemptReconnect();
        } else {
          this.status = 'disconnected';
        }
      };
    } catch (error) {
      console.error('Error creating WebSocket:', error);
      this.status = 'disconnected';
      this.attemptReconnect();
    }
  }
  
  startHeartbeat() {
    // Clear any existing heartbeat first
    this.stopHeartbeat();
    
    this.heartbeatInterval = setInterval(() => {
      if (this.socket && this.socket.readyState === WebSocket.OPEN) {
        // Send a ping message
        this.socket.send(JSON.stringify({ type: 'ping' }));
        // Set a timeout waiting for the pong response
        this.heartbeatTimeout = setTimeout(() => {
          console.warn("Heartbeat timeout: no pong received. Closing socket.");
          this.socket.close();
        }, this.heartbeatTimeoutTime);
      }
    }, this.heartbeatIntervalTime);
  }
  
  stopHeartbeat() {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
    if (this.heartbeatTimeout) {
      clearTimeout(this.heartbeatTimeout);
      this.heartbeatTimeout = null;
    }
  }
  
  attemptReconnect() {
    this.status = 'reconnecting';
    this.reconnectAttempts++;
    
    const delay = Math.min(
      30000, 
      Math.pow(2, this.reconnectAttempts) * 1000 + Math.random() * 1000
    );
    
    console.log(`Attempting to reconnect in ${delay}ms (attempt ${this.reconnectAttempts})`);
    this.notifyListeners('reconnecting', { attempt: this.reconnectAttempts, delay });
    
    this.reconnectTimeoutId = setTimeout(() => {
      this.connect();
    }, delay);
  }
  
  send(data) {
    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      if (typeof data === 'object') {
        this.socket.send(JSON.stringify(data));
      } else {
        this.socket.send(data);
      }
      return true;
    } else {
      console.warn('Cannot send message - WebSocket not connected');
      return false;
    }
  }
  
  addListener(event, callback) {
    if (this.listeners[event]) {
      this.listeners[event].push(callback);
      return true;
    }
    return false;
  }
  
  removeListener(event, callback) {
    if (this.listeners[event]) {
      this.listeners[event] = this.listeners[event].filter(cb => cb !== callback);
      return true;
    }
    return false;
  }
  
  notifyListeners(event, data) {
    if (this.listeners[event]) {
      this.listeners[event].forEach(callback => {
        try {
          callback(data);
        } catch (error) {
          console.error(`Error in ${event} listener:`, error);
        }
      });
    }
  }
  
  cleanup() {
    try {
      if (this.socket) {
        this.socket.onopen = null;
        this.socket.onmessage = null;
        this.socket.onerror = null;
        this.socket.onclose = null;
        if (this.socket.readyState !== WebSocket.CLOSED) {
          this.socket.close();
        }
        this.socket = null;
      }
    } catch (error) {
      console.error('Error during WebSocket cleanup:', error);
    }
  }
  
  disconnect() {
    this.intentionallyClosed = true;
    if (this.reconnectTimeoutId) {
      clearTimeout(this.reconnectTimeoutId);
      this.reconnectTimeoutId = null;
    }
    this.stopHeartbeat();
    this.cleanup();
    this.status = 'disconnected';
  }
}
